<template>
    <div class="px-8 pb-3 pt-5">
        <h3 class="mb-2 text-[16px] font-semibold">Early Deposit</h3>
        <DataTable
            :data-key="data => data.id"
            :pt="subtableTheadStyles"
            :row-class="getClientRowClasses"
            :value="rowData.clients"
            @animationend="onClientAnimationEnd"
        >
            <Column
                body-class="pr-4 border-x-transparent align-top"
                field="role"
                header="Sender"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div class="font-semibold capitalize">{{ clientData.role }}</div>
                    <div v-if="clientData.first">{{ clientData.first }} {{ clientData.last }}</div>
                    <div>{{ clientData.email }}</div>
                </template>
            </Column>
            <Column
                :body-class
                header="Amount"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div v-if="!clientData.pre_file_payments?.length">--</div>
                    <div
                        v-for="payment in clientData.pre_file_payments"
                        :key="`amount-${payment.id}`"
                    >
                        {{ getPreFilePaymentAmountFormattedString(payment) }}
                    </div>
                </template>
            </Column>
            <Column
                :body-class
                field="created_at"
                header="Requested Date"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div v-if="!clientData.pre_file_payments?.length">
                        {{ formatDate(rowData.created_at) }}
                    </div>
                    <div
                        v-for="payment in clientData.pre_file_payments"
                        :key="`requested-date-${payment.id}`"
                    >
                        {{ formatDate(rowData.created_at) }}
                    </div>
                </template>
            </Column>
            <Column
                :body-class
                field="payment_sent_date"
                header="Sent Date"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div v-if="!clientData.pre_file_payments?.length">--</div>
                    <div
                        v-for="payment in clientData.pre_file_payments"
                        :key="`sent-date-${payment.id}`"
                    >
                        {{ payment.payment_created_at ?? '--' }}
                    </div>
                </template>
            </Column>
            <Column
                :body-class
                field="payment_status"
                header="Status"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div v-if="!clientData.pre_file_payments?.length">Requested</div>
                    <div
                        v-for="payment in clientData.pre_file_payments"
                        :key="`status-${payment.id}`"
                    >
                        {{ payment.status }}
                    </div>
                </template>
            </Column>
            <Column
                :body-class
                header="Actions"
                header-class="text-transparent"
                :pt="headerCellStyleOverrides"
            >
                <template #body="{ data: clientData }: ClientRowData">
                    <div
                        v-if="!clientData.pre_file_payments?.length"
                        class="text-center"
                    >
                        <EarlyDepositRowActions
                            :client="clientData"
                            :emd="rowData"
                            :payment="null"
                        />
                    </div>
                    <div
                        v-else
                        v-for="payment in clientData.pre_file_payments"
                        :key="`actions-${payment.id}`"
                        class="text-center"
                    >
                        <EarlyDepositRowActions
                            :client="clientData"
                            :emd="rowData"
                            :payment
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <h3 class="mb-2 mt-6 font-inter text-[16px] font-semibold">Notification Recipients</h3>
        <DataTable
            :pt="subtableTheadStyles"
            :value="rowData.notifying_users"
        >
            <Column
                :body-class
                field="email"
                header="Email"
                :pt="headerCellStyleOverrides"
            />
            <Column
                :body-class
                field="name"
                header="Name"
                :pt="headerCellStyleOverrides"
            />
        </DataTable>
    </div>

    <DeleteClientEarlyDepositModal @emdClientDeleted="onEmdClientDeleted" />
</template>

<script setup lang="ts">
import type { EarlyDeposit } from '@/api/interfaces/early-deposit-types';
import DeleteClientEarlyDepositModal from '@/components/Views/Company/DeleteClientEarlyDepositModal.vue';
import EarlyDepositRowActions from '@/components/Views/Company/EarlyDepositRowActions.vue';
import { useEarlyDepositsPaged } from '@/services/early-deposit.service';
import { formatDate } from '@/shared/utils/format-date';
import { formatMoney } from '@/shared/utils/format-number';
import type { Client, PreFilePayment } from '@/types/closinglock.types';
import Column from 'primevue/column';
import DataTable, { type DataTableProps } from 'primevue/datatable';
import { reactive } from 'vue';

const props = defineProps<{ rowData: EarlyDeposit }>();

type ClientRowData = { data: EarlyDeposit['clients'][number] };

const bodyClass = 'border-x-transparent align-top';
const subtableTheadStyles: DataTableProps['pt'] = {
    thead: 'bg-ds-primary-100 border-t border-t-ds-primary-700 font-inter',
} as const;
const headerCellStyleOverrides: DataTableProps['pt'] = {
    headerCell: 'bg-transparent border-b-transparent py-3 text-sm',
} as const;

const highlightModifiedClient: { id: Client['id']; operation: '' | 'delete' } = reactive({
    id: '',
    operation: '',
});

const getPreFilePaymentAmountFormattedString = (preFilePayment?: Pick<PreFilePayment, 'payment_base_amount'>) => {
    return preFilePayment?.payment_base_amount ? formatMoney(preFilePayment.payment_base_amount / 100) : '--';
};

const { updateLocalEmd } = useEarlyDepositsPaged();

const onEmdClientDeleted = (deleteClientRef: Client['id']) => {
    highlightModifiedClient.id = deleteClientRef;
    highlightModifiedClient.operation = 'delete';
};

const getClientRowClasses = (clientData: EarlyDeposit['clients'][number]) => {
    const RECENTLY_DELETED_CSS = `play-emd-client-deleted`;
    const modifiedId = Number(highlightModifiedClient.id);
    const operation = highlightModifiedClient.operation;

    return {
        [`transition-color duration-100 client-row-id-${clientData.id}`]: true,
        'hover:bg-ds-blue-100/50': clientData.id !== modifiedId,
        [RECENTLY_DELETED_CSS]: operation === 'delete' && clientData.id === modifiedId,
    };
};

const onClientAnimationEnd = async ({ animationName, target }: AnimationEvent) => {
    const isClientDeleteAnimation = animationName.startsWith('client-deleted');
    if (!isClientDeleteAnimation) return;

    if ((target as HTMLElement).classList.contains('client-row-id-' + highlightModifiedClient.id)) {
        updateLocalEmd({
            id: props.rowData.id,
            clients: props.rowData.clients.filter(client => client.id !== Number(highlightModifiedClient.id)),
        });
    }

    highlightModifiedClient.id = '';
    highlightModifiedClient.operation = '';
};
</script>

<style scoped lang="scss">
:deep(.font-inter) {
    button,
    input,
    optgroup,
    select,
    textarea,
    span,
    label {
        font-family: inherit;
    }
}

:deep(.play-emd-client-deleted) {
    animation: client-deleted 1s forwards;
}

@keyframes client-deleted {
    from {
        @apply bg-ds-red-100;
    }

    to {
        @apply line-through;
        opacity: 0;
    }
}
</style>
